// src/pages/app/BotPlanPage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Bot,
  getBotById,
  fetchUsagePlans,
  UsagePlan,
  startBotCheckout,
  changeBotUsagePlan,
  getBotPricingPreview
} from "../../api/bots";
import { useTranslation } from "react-i18next";

import starterImg from "../../assets/coslo-assist-247.png";
import growthImg from "../../assets/coslo-assist-247.png";
import scaleImg from "../../assets/coslo-assist-247.png";
import defaultPlanImg from "../../assets/coslo-assist-247.png";

import { getStoredReferralCode } from "../../utils/referral";

const formatCurrency = (amountCents: number, currency: string) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2
  }).format(amountCents / 100);

const planImages: Record<string, string> = {
  free: starterImg,
  starter: starterImg,
  basic: starterImg,
  growth: growthImg,
  pro: growthImg,
  scale: scaleImg,
  enterprise: scaleImg
};

const getPlanImage = (plan: UsagePlan): string => {
  const key = plan.name.toLowerCase();
  return planImages[key] ?? defaultPlanImg;
};

const BotPlanPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [bot, setBot] = useState<Bot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [plans, setPlans] = useState<UsagePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPlansLoading(true);

    Promise.all([getBotById(id), getBotPricingPreview(id, {}), fetchUsagePlans()])
      .then(([botData, pricing, plansData]) => {
        setBot(botData);
        setPlans(plansData);
        if (plansData.length > 0) setSelectedPlanId(plansData[0].id);
      })
      .catch((err: any) => {
        console.error(err);
        setError(err?.message || t("botPlan.errors.loadFailed"));
      })
      .finally(() => {
        setLoading(false);
        setPlansLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSelectPlan = (planId: string) => {
    setSelectedPlanId(planId);
  };

  if (!id) {
    return (
      <div className="page-container">
        <p>{t("botPlan.missingId")}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <p>{t("botPlan.loading")}</p>
      </div>
    );
  }

  if (error && !bot) {
    return (
      <div className="page-container">
        <h1>{t("botPlan.errorTitle")}</h1>
        <p>{error}</p>
      </div>
    );
  }

  if (!bot) {
    return (
      <div className="page-container">
        <h1>{t("botPlan.notFoundTitle")}</h1>
      </div>
    );
  }

  const isActive = bot.status === "ACTIVE";
  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;

  const handleActivateOrChangePlan = async () => {
    if (!id || !bot) return;
    if (!selectedPlanId) {
      setError(t("botPlan.errors.selectPlan"));
      return;
    }

    setCheckoutLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!isActive) {
        // Attach referralCode if present (safe, won’t break)
        const referralCode = getStoredReferralCode();
        const payload: Record<string, unknown> = { usagePlanId: selectedPlanId };
        if (referralCode) payload.referralCode = referralCode;

        const { checkoutUrl } = await startBotCheckout(id, payload as any);
        window.location.href = checkoutUrl;
      } else {
        await changeBotUsagePlan(id, { usagePlanId: selectedPlanId });
        setSuccess(t("botPlan.success.planUpdatedProration"));

        const updatedBot = await getBotById(id);
        setBot(updatedBot);
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botPlan.errors.updateFailed"));
    } finally {
      setCheckoutLoading(false);
    }
  };

  const getStatusPillClass = (status: Bot["status"]) => {
    const normalized = status.toUpperCase();
    if (normalized === "ACTIVE") return "plan-summary-status plan-summary-status-ok";
    if (normalized === "DRAFT") return "plan-summary-status plan-summary-status-warn";
    if (normalized === "CANCELLED" || normalized === "INACTIVE")
      return "plan-summary-status plan-summary-status-error";
    return "plan-summary-status";
  };

  const statusPillClass = getStatusPillClass(bot.status);

  const combinedTotalFormatted =
    selectedPlan
      ? formatCurrency(selectedPlan.monthlyAmountCents, selectedPlan.currency)
      : null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>{t("botPlan.title")}</h1>
          <p className="muted">{t("botPlan.subtitle", { botName: bot.name })}</p>
        </div>

        <div className="header-actions">
          <button
            className="btn-secondary"
            type="button"
            onClick={() => navigate(`/app/bots/${bot.id}/features`)}
          >
            {t("botPlan.backToFeatures")}
          </button>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}
      {success && <div className="form-success">{success}</div>}

      <div className="detail-layout">
        <section className="detail-main">
          <h2>{t("botPlan.plans.title")}</h2>
          <p className="usage-plans-subtitle">{t("botPlan.plans.subtitle")}</p>

          {plansLoading && <p>{t("botPlan.plans.loading")}</p>}
          {plansError && <p className="form-error">{plansError}</p>}

          {plans.length === 0 && !plansLoading && (
            <p className="muted">{t("botPlan.plans.empty")}</p>
          )}

          <div className="plan-cards">
            {plans.map((plan) => {
              const isSelected = plan.id === selectedPlanId;

              return (
                <div
                  key={plan.id}
                  className={`plan-card ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelectPlan(plan.id)}
                >
                  <div className="plan-card-media">
                    <img
                      src={getPlanImage(plan)}
                      alt={t("botPlan.plans.planImageAlt", { planName: plan.name })}
                    />
                  </div>

                  <div className="plan-card-header">
                    <div className="plan-card-name">{plan.name}</div>
                    <div className="plan-card-price">
                      {formatCurrency(plan.monthlyAmountCents, plan.currency)}/{t("botPlan.perMonth")}
                    </div>
                  </div>

                  {plan.description && (
                    <p className="plan-card-description">{plan.description}</p>
                  )}

                  <ul className="plan-limits">
                    <li>
                      {t("botPlan.plans.tokensPerMonth")}{" "}
                      <strong>{plan.monthlyTokens ?? t("botPlan.plans.unlimited")}</strong>
                    </li>
                  </ul>

                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectPlan(plan.id);
                    }}
                  >
                    {isSelected ? t("botPlan.plans.selected") : t("botPlan.plans.select")}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <section className="detail-side">
          <div className="plan-summary-header">
            <h2 style={{ margin: 0 }}>{t("botPlan.summary.title")}</h2>
            <span className={statusPillClass}>
              {t("botPlan.summary.statusLabel")} <strong>{bot.status}</strong>
            </span>
          </div>

          <p className="muted" style={{ marginTop: "0.35rem" }}>
            {t("botPlan.summary.subtitle")}
          </p>

          <h3 style={{ marginTop: "0.9rem", marginBottom: "0.35rem" }}>
            {t("botPlan.summary.featuresSubtotal")}
          </h3>

          <h3 style={{ marginTop: "1.4rem", marginBottom: "0.3rem" }}>
            {t("botPlan.summary.selectedPlan")}
          </h3>

          {selectedPlan ? (
            <div className="plan-summary-lines">
              <div className="plan-summary-line">
                <span className="plan-summary-line-label">{selectedPlan.name}</span>
                <span className="plan-summary-line-amount">
                  {formatCurrency(selectedPlan.monthlyAmountCents, selectedPlan.currency)}/{t("botPlan.perMonth")}
                </span>
              </div>

              <div className="plan-summary-line">
                <span className="plan-summary-line-label">
                  {t("botPlan.plans.tokensPerMonthLabel")}
                </span>
                <span className="plan-summary-line-amount">
                  {selectedPlan.monthlyTokens ?? t("botPlan.plans.unlimited")}
                </span>
              </div>
            </div>
          ) : (
            <p className="muted">{t("botPlan.summary.noPlanSelected")}</p>
          )}

          <div className="plan-summary-total" style={{ marginTop: "1.4rem" }}>
            <span>{t("botPlan.summary.totalPerMonth")}</span>
            <strong>{combinedTotalFormatted ?? t("botPlan.summary.selectPlanToSeeTotal")}</strong>
          </div>

          {!isActive && (
            <>
              <button
                className="btn-primary"
                type="button"
                onClick={handleActivateOrChangePlan}
                disabled={checkoutLoading || !selectedPlan}
                style={{ marginTop: "1rem" }}
              >
                {checkoutLoading ? t("botPlan.actions.redirecting") : t("botPlan.actions.activatePay")}
              </button>

              <p className="detail-side-note">{t("botPlan.notes.redirectToStripe")}</p>
            </>
          )}

          {isActive && (
            <>
              <button
                className="btn-primary"
                type="button"
                onClick={handleActivateOrChangePlan}
                disabled={checkoutLoading || !selectedPlan}
                style={{ marginTop: "1rem" }}
              >
                {checkoutLoading ? t("botPlan.actions.updating") : t("botPlan.actions.changePlan")}
              </button>

              <p className="detail-side-note">{t("botPlan.notes.proration")}</p>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default BotPlanPage;
